// Adventure system — the cat leaves the kingdom for a quest in a different
// biome. Pieces:
//
//   • start(advId, mode) — kicks off a quest. Persists profile.adventure
//     with startedAt/endsAt timestamps. Emits AdventureStart.
//   • recall()           — return early. Pays out a partial reward.
//   • complete()         — fires automatically when the timer elapses. Full
//                          reward + lifetime tally + AdventureEnd event.
//   • setMode(mode)      — DISCOVERY vs DEFENSE. Bound to keys 1/2 elsewhere.
//   • currentAdventure() — returns the active record, or null.
//   • init()             — wires the per-second tick + auto-completion + the
//                          scribe-beat scheduler + the discovery roller.
//
// Time accounting is wall-clock (Date.now). The tick re-evaluates remaining
// time, fires beats whose `atSec` has been crossed, and rolls discoveries on
// the configured cadence while in DISCOVERY mode. Reloads survive: the
// adventure record holds startedAt/endsAt + scribeBeatsFired so we can resume
// without re-firing past beats.

import { Events, emit, on } from '../ui/eventBus.js'
import { AdventureConfig, AdventureModes, AdventureRules } from '../config/balance.js'
import { getProfile, persist } from './state.js'
import { earnWealth, spendWealth, getWealth } from './seasonManager.js'
import * as toolMode from './toolMode.js'
import { damageCount } from './damageManager.js'
import { getBuildings } from './buildingState.js'
import { BuildingConfig } from '../config/balance.js'
import { formatCoins } from '../ui/format.js'

// The adventure stance mirrors the global tool mode (the 1/2 keys):
//   DEFENSE  ↔ ToolMode DEFENSE  (red — raid severity ×0.4)
//   DISCOVERY↔ ToolMode POSITIVE (yellow — rolls treasure)
//   NEUTRAL  ↔ ToolMode OFF      (no reticle — no rolls, full raids)
function stanceToTool(stance) {
  return stance === AdventureModes.DEFENSE ? toolMode.ToolModes.DEFENSE
    : stance === AdventureModes.DISCOVERY ? toolMode.ToolModes.POSITIVE
    : toolMode.ToolModes.OFF
}
function toolToStance(mode) {
  return mode === toolMode.ToolModes.DEFENSE ? AdventureModes.DEFENSE
    : mode === toolMode.ToolModes.POSITIVE ? AdventureModes.DISCOVERY
    : AdventureModes.NEUTRAL
}

// Mirror tool-mode changes onto the active adventure's stance so raid severity
// + discovery rolls follow the 1/2 keys. Fires AdventureModeChange so the HUD
// chip + panel update.
on(Events.ToolModeChange, ({ mode }) => {
  const a = currentAdventure()
  if (!a) return
  const stance = toolToStance(mode)
  if (a.mode === stance) return
  a.mode = stance
  persist()
  emit(Events.AdventureModeChange, { mode: stance })
})

const TICK_MS = 1000

// Currency-only payout when we finish (recall or auto-complete). On a recall
// we return only the fraction of `progress` already elapsed; on auto-complete
// the player gets the full sum of rolled discoveries.
function totalCoin(discoveries = []) {
  return discoveries.reduce((s, d) => s + (d.amount ?? 0), 0)
}

function defOf(id) {
  return AdventureConfig.find((a) => a.id === id) ?? null
}

export function currentAdventure() {
  return getProfile().adventure ?? null
}

export function currentDef() {
  const a = currentAdventure()
  return a ? defOf(a.id) : null
}

export function isAdventuring() {
  return !!currentAdventure()
}

export function getMode() {
  return currentAdventure()?.mode ?? null
}

// Begin an adventure. No-op if one is already active. Stamps the active
// adventure on the profile + fires AdventureStart.
export function start(advId, mode = AdventureModes.DISCOVERY) {
  if (isAdventuring()) return { ok: false, reason: 'already adventuring' }
  const def = defOf(advId)
  if (!def) return { ok: false, reason: 'unknown adventure' }
  const now = Date.now()
  const adventure = {
    id: advId,
    startedAt: now,
    endsAt: now + def.duration * 1000,
    mode,
    discoveries: [],
    scribeBeatsFired: {},
    lastDiscoveryAt: now,
  }
  getProfile().adventure = adventure
  persist()
  emit(Events.AdventureStart, { adventure, mode })
  // Drive the global tool mode → reticle reflects the chosen stance. The
  // ToolModeChange listener mirrors it straight back onto a.mode (no-op).
  toolMode.setMode(stanceToTool(mode))
  return { ok: true, adventure }
}

// Set the adventure stance directly (used by the panel's mode buttons). Routes
// through the global tool mode so the reticle + 1/2 keys stay in sync.
export function setMode(stance) {
  if (!currentAdventure()) return
  toolMode.setMode(stanceToTool(stance))
}

// Recall — return early. Pays out the accumulated discoveries as cash plus
// a small bonus proportional to remaining time NOT served (the cat got out
// alive, after all). AdventureEnd with reason='recall' so listeners can
// distinguish from a clean completion.
export function recall() {
  const a = currentAdventure()
  if (!a) return { ok: false, reason: 'no active adventure' }
  const def = defOf(a.id)
  const payout = Math.round(totalCoin(a.discoveries) * AdventureRules.recallPayoutPct)
  if (payout > 0) earnWealth(payout)
  return finishAdventure(a, def, { reason: 'recall', payout })
}

// Abandon — the panic button. The cat drops its haul and SPRINTS home (e.g.
// to defend an active raid). Penalty vs recall: forfeit ALL discovered coins
// AND relics from this trip, and pay a flat "scramble fine" from the treasury
// (clamped to available wealth so it can't push you negative). Reason
// 'abandon' lets listeners show the distinct outcome.
export function abandon() {
  const a = currentAdventure()
  if (!a) return { ok: false, reason: 'no active adventure' }
  const def = defOf(a.id)
  const fine = Math.min(def?.abandonFine ?? 0, getWealth())
  if (fine > 0) spendWealth(fine)
  // payout 0, relics dropped (keepRelics: false).
  return finishAdventure(a, def, { reason: 'abandon', payout: 0, keepRelics: false, fine })
}

// Auto-completion path — full payout, lifetime tally bumps. Called from the
// tick when wall-clock crosses endsAt.
function autoComplete(a, def) {
  const payout = totalCoin(a.discoveries)
  if (payout > 0) earnWealth(payout)
  return finishAdventure(a, def, { reason: 'complete', payout })
}

function finishAdventure(a, def, { reason, payout, keepRelics = true, fine = 0 }) {
  const profile = getProfile()
  // Home again — clear the tool mode so the reticle disappears.
  toolMode.setMode(toolMode.ToolModes.OFF)
  // Drop the discoveries into the permanent bag so the Registry can show
  // them later. Bag entries stay forever — they're the cat's relic record.
  // Abandon (keepRelics=false) loses everything found this trip.
  if (keepRelics) {
    const bagAdds = a.discoveries.map((d) => ({
      id: `${a.id}:${d.at}:${Math.random().toString(36).slice(2, 7)}`,
      item: d.item,
      amount: d.amount,
      foundAt: d.at,
      biome: def?.biome ?? 'unknown',
    }))
    profile.adventureBag = [...(profile.adventureBag ?? []), ...bagAdds]
  }
  profile.adventure = null
  persist()
  // AdventureCompleted counter fires only on full completion (not recall) so
  // achievements gate on actual finishes, not panicked recalls.
  if (reason === 'complete') {
    // Lifetime counter for the achievement ladder. Also stamp a per-biome
    // completion record on the profile so the legendary "all biomes" badge
    // has a place to read from.
    emit(Events.AdventureCompleted, { adventure: a, biome: def?.biome })
    profile.adventureBiomesCompleted = profile.adventureBiomesCompleted ?? {}
    if (def?.biome) profile.adventureBiomesCompleted[def.biome] = Date.now()
    persist()
  }
  emit(Events.AdventureEnd, {
    adventure: a,
    reason,
    payout,
    fine,
    discoveries: a.discoveries,
  })
  return { ok: true, payout, reason, fine }
}

// ── Tick loop: scribe beats + discovery rolls + auto-complete ───────────────

function rollDiscovery(def) {
  const table = def.discoveryTable ?? []
  if (!table.length) return null
  const totalWeight = table.reduce((s, e) => s + (e.weight ?? 0), 0)
  let r = Math.random() * totalWeight
  for (const entry of table) {
    r -= entry.weight
    if (r <= 0) {
      const amount = Math.round(
        entry.minCoin + Math.random() * (entry.maxCoin - entry.minCoin),
      )
      return { item: entry.item, amount }
    }
  }
  return null
}

function tick() {
  const a = currentAdventure()
  if (!a) return
  const def = defOf(a.id)
  if (!def) return
  const now = Date.now()
  const elapsedSec = Math.max(0, (now - a.startedAt) / 1000)
  const remaining = Math.max(0, (a.endsAt - now) / 1000)
  const total = def.duration

  rollRaid(a, def, now)

  // Auto-complete when wall-clock crosses endsAt. Catch up beats and one last
  // discovery roll BEFORE finishing so the player isn't shortchanged by the
  // tab being backgrounded.
  if (now >= a.endsAt) {
    fireDueScribeBeats(a, def, total) // all remaining beats fire
    autoComplete(a, def)
    return
  }

  fireDueScribeBeats(a, def, elapsedSec)

  // DISCOVERY mode rolls on a fixed cadence. We compare against the last roll
  // time so a long-backgrounded tab still gets credit for missed ticks.
  if (a.mode === AdventureModes.DISCOVERY) {
    const tickSec = def.discoveryTickSec ?? 30
    while (now - a.lastDiscoveryAt >= tickSec * 1000 && now < a.endsAt) {
      const drop = rollDiscovery(def)
      a.lastDiscoveryAt += tickSec * 1000
      if (drop) {
        const at = now
        a.discoveries.push({ at, item: drop.item, amount: drop.amount })
        emit(Events.AdventureDiscovery, { item: drop.item, amount: drop.amount, biome: def.biome })
      }
    }
    persist()
  }

  emit(Events.AdventureTick, {
    elapsed: elapsedSec,
    remaining,
    progress: Math.min(1, elapsedSec / total),
  })
}

// Home raid roll — fires Events.RaidStart based on dangerProfile, which the
// scene's wildGoons listener picks up to spawn raiders. DEFENSE mode halves
// the per-tick chance so the player can throttle risk.
//
// Cooldown: at most one raid per ~45s of adventure time so the player isn't
// drowned in events on long expeditions.
function rollRaid(a, def, now) {
  if (a.lastRaidAt && now - a.lastRaidAt < 45_000) return
  const perMin = def.dangerProfile?.raidChancePerMin ?? 0
  if (perMin <= 0) return
  // Convert per-minute → per-tick (TICK_MS).
  const perTick = (perMin * TICK_MS) / 60_000
  const stanceMult = a.mode === AdventureModes.DEFENSE ? 0.4 : 1.0
  if (Math.random() < perTick * stanceMult) {
    a.lastRaidAt = now
    a.raidCount = (a.raidCount ?? 0) + 1
    persist()
    emit(Events.RaidStart, { source: 'adventure', adventureId: a.id })
    // Auto-end the raid 30s later so the player can see resolution.
    setTimeout(() => emit(Events.RaidEnd, { source: 'adventure' }), 30_000)
  }
}

// The scribe never lies: every report is computed from what really happened at home. No canned warnings.
function homeReport(a, def) {
  const raids = a.raidCount ?? 0
  const sites = damageCount()
  const left = Math.max(0, Math.round((a.endsAt - Date.now()) / 1000))
  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`
  if (!raids && !sites) return `Home report: calm so far — no raids, nothing damaged. ${mmss} left on this adventure.`
  const dmg = sites ? ` ${sites} site${sites === 1 ? '' : 's'} damaged — repairs wait for your return.` : ' Nothing damaged.'
  return `Home report: ${raids} raid${raids === 1 ? '' : 's'} so far.${dmg} ${mmss} left — RECALL to hurry back (you keep 70% of the haul).`
}

// Real events at home while you're away → real alerts, each with what to do.
function wireHomeAlerts() {
  const active = () => currentAdventure()
  on(Events.RaidStart, (p) => {
    if (!active() || p?.source !== 'adventure') return
    emit(Events.AdventureScribeBeat, { kind: 'smoke', message: 'Smoke signal: raiders are loose at home. Press RECALL to rush back and defend (you keep 70% of the haul), or stay and repair after.' })
  })
  on(Events.BuildingDamaged, ({ tileIndex }) => {
    if (!active()) return
    const b = getBuildings().find((x) => String(x.tileIndex) === String(tileIndex))
    const name = BuildingConfig[b?.type]?.name ?? 'building'
    emit(Events.AdventureScribeBeat, { kind: 'smoke', message: `Smoke signal: your ${name} at home was torched. It gives no bonus until repaired — repair it when you return.` })
  })
  on(Events.PlotDamaged, () => {
    if (!active()) return
    emit(Events.AdventureScribeBeat, { kind: 'smoke', message: 'Smoke signal: a field at home is burning. Repair it when you return.' })
  })
  on(Events.RaidEnd, (p) => {
    if (!active() || p?.source !== 'adventure') return
    const n = damageCount()
    emit(Events.AdventureScribeBeat, { kind: 'courier', message: `Courier: the raid has ended. ${n ? `${n} site${n === 1 ? '' : 's'} damaged — repair on your return.` : 'Nothing was damaged.'}` })
  })
}

function fireDueScribeBeats(a, def, elapsedSec) {
  const beats = def.scribeBeats ?? []
  for (const beat of beats) {
    if (elapsedSec < beat.atSec) continue
    if (a.scribeBeatsFired[beat.atSec]) continue
    a.scribeBeatsFired[beat.atSec] = true
    emit(Events.AdventureScribeBeat, { kind: beat.kind, message: homeReport(a, def) })
  }
  persist()
}

let timer = null

export function init() {
  // Resume an in-progress adventure on boot — re-stamp lastDiscoveryAt if it
  // was lost (older saves) and start ticking.
  const a = currentAdventure()
  if (a && !a.lastDiscoveryAt) a.lastDiscoveryAt = a.startedAt ?? Date.now()
  if (timer) clearInterval(timer)
  timer = setInterval(tick, TICK_MS)
  wireHomeAlerts()

  // If a quest is already underway (page reload mid-adventure), re-broadcast
  // AdventureStart so every listener — the biome scene-swap, the HUD status
  // chip, the panel — re-syncs to the active state. The adventure is NOT
  // restarted; the existing record (startedAt/endsAt/discoveries) is kept.
  // Listeners must be subscribed before init() runs; boot order guarantees
  // the scene/hud/panel are all mounted first.
  if (a && !a.endsAt) a.endsAt = (a.startedAt ?? Date.now()) + 60_000 // safety for malformed saves
  if (a && Date.now() < a.endsAt) {
    emit(Events.AdventureStart, { adventure: a, mode: a.mode, resumed: true })
    // Restore the reticle to the persisted stance after a reload.
    toolMode.setMode(stanceToTool(a.mode))
  }
  // If the adventure should already have ended while the tab was closed,
  // the first tick() settles it (payout + AdventureEnd) — no resume needed.
}
